/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Typography, TextField, Grid, Popover, Tooltip, Button,
} from '@mui/material';
import { ChromePicker } from 'react-color';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import InfoIcon from '@mui/icons-material/Info';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import SettingsMenu from './components/SettingsMenu';
import UploadAvatar from '../common/components/upload/UploadAvatar';
import palette from '../common/theme/palette';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useCatch } from '../reactHelper';
import { useAdministrator } from '../common/util/permissions';
import { useTheme } from '@mui/styles';
import { MuiFileInput } from 'mui-file-input';

const useStyles = makeStyles((theme) => ({
  details: {
    display: 'flex',
    flexDirection: 'column',
  },
  iconPreview: {
    width: 64,
    height: 64,
    margin: '4px',
    objectFit: 'contain',
  },
}));

const RevendaPage = () => {
  const classes = useStyles();
  const t = useTranslation();
  const admin = useAdministrator();
  const theme = useTheme();

  const [item, setItem] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentColorField, setCurrentColorField] = useState(null);
  const [backgroundAvailable, setBackgroundAvailable] = useState({
    light: false,
    dark: false
  });
  const [iconFile, setIconFile] = useState(null);

  // Padrões de gradiente
  const lightPattern = `linear-gradient(135deg, #e6f0ff 0%, #d0e1ff 25%, #ffffff 50%, #e6f0ff 100%),
    radial-gradient(circle at 10% 20%, rgba(255,255,255,0.1) 0%, transparent 20%),
    radial-gradient(circle at 90% 80%, rgba(255,255,255,0.1) 0%, transparent 20%)`;

  const darkPattern = `linear-gradient(135deg, #0a192f 0%, #0d1f3a 25%, #122b4a 50%, #0a192f 100%),
    radial-gradient(circle at 10% 20%, rgba(0,184,212,0.05) 0%, transparent 20%),
    radial-gradient(circle at 90% 80%, rgba(0,184,212,0.05) 0%, transparent 20%)`;

  // Determina o modo atual com fallback seguro
  const currentMode = theme?.palette?.mode || palette?.mode || 'light';

  // Função para verificar se a imagem existe
  const checkImage = useCallback((src) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });
  }, []);

  // Verificar se as imagens de background existem
  const verifyBackgrounds = useCallback(async () => {
    if (!item?.code) return;

    const lightUrl = `./themes/${item.code}/imagens/light-background.webp`;
    const darkUrl = `./themes/${item.code}/imagens/dark-background.webp`;

    const [lightOk, darkOk] = await Promise.all([
      checkImage(lightUrl),
      checkImage(darkUrl)
    ]);

    setBackgroundAvailable({ light: lightOk, dark: darkOk });
  }, [item?.code, checkImage]);

  // Executar a verificação quando o código da revenda mudar
  useEffect(() => {
    verifyBackgrounds();
  }, [verifyBackgrounds]);

  const getLogo = (rev) => {
    if (rev.logoPreview) {
      return { preview: rev.logoPreview };
    }
    return `/api/revendas/logo/${rev.code}`;
  };

  const handleColorClick = (field, event) => {
    setCurrentColorField(field);
    setAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setAnchorEl(null);
    setCurrentColorField(null);
  };

  const handleColorChange = (color) => {
    setItem({ ...item, [currentColorField]: color.hex });
    if (currentColorField === 'background') {
      verifyBackgrounds();
    }
  };

  const handleLogoDrop = useCatch(async (acceptedFiles) => {
    if (!item?.id || acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    const response = await fetch(`/api/revendas/${item.id}/logo`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type
      },
      body: file,
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    const filename = await response.text();
    const previewUrl = URL.createObjectURL(file);
    setItem({ ...item, logo: filename, logoPreview: previewUrl });
  });

  const handleIconInput = useCatch(async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setIconFile(file);

        // Armazena a imagem em base64 nos atributos
        const attributes = {
          ...item.attributes,
          iconBase64: base64,
          iconFileName: file.name
        };
        setItem({ ...item, attributes });
      };
      reader.readAsDataURL(file);
    }
  });

  const getIconPreview = () => {
    if (iconFile) {
      return URL.createObjectURL(iconFile);
    }
    if (item?.attributes?.iconBase64) {
      return item.attributes.iconBase64;
    }
    return null;
  };

  const validate = () => item && item.code && item.title && item.url;

  return (
    <EditItemView
      endpoint="revendas"
      item={item}
      setItem={setItem}
      validate={validate}
      menu={<SettingsMenu />}
      breadcrumbs={['userAdmin', 'sharedRevendas', 'sharedRevenda']}
    >
      {item && (
        <>
          {/* Seção Obrigatória */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedRequired') || 'Campos Obrigatórios'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                width="100%"
                gap={2}
                sx={{
                  flexDirection: { xs: 'column', sm: 'row' }
                }}
              >
                {/* Logo - Ocupa 1/3 do espaço em desktop */}
                <Box flex={1} minWidth={0}>
                  <Box sx={{ mb: 4 }}>
                    <UploadAvatar
                      accept="image/*"
                      file={getLogo(item)}
                      maxSize={Number.MAX_SAFE_INTEGER}
                      onDrop={handleLogoDrop}
                      caption={t('attributeResaleLogo')}
                    />
                    <Typography
                      variant="caption"
                      style={{ display: 'block', textAlign: 'center', color: 'text.secondary' }}
                    >
                      {t('sharedAllowed') || 'Qualquer tipo de arquivo permitido'}<br />
                      {t('sharedMaxSize') || 'Sem limite de tamanho prático'}
                    </Typography>
                  </Box>
                </Box>

                {/* Campos de texto - Ocupam 2/3 do espaço em desktop */}
                <Box flex={2} minWidth={0}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                  >
                    {admin && (
                      <TextField
                        fullWidth
                        margin="normal"
                        value={item.code || ''}
                        onChange={(e) => setItem({ ...item, code: e.target.value })}
                        label={t('sharedCodeInternal') || 'Código Interno'}
                      />
                    )}
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.title || ''}
                      onChange={(e) => setItem({ ...item, title: e.target.value })}
                      label={t('sharedName') || 'Nome'}
                    />
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.url || ''}
                      onChange={(e) => setItem({ ...item, url: e.target.value })}
                      label={t('sharedUrl') || 'URL'}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Seção Informações */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedInfo') || 'Informações'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                width="100%"
              >
                {/* Primeira linha */}
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.template || ''}
                      onChange={(e) => setItem({ ...item, template: e.target.value })}
                      label={t('sharedTemplate') || 'Nome'}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.phone || ''}
                      onChange={(e) => setItem({ ...item, phone: e.target.value })}
                      label={t('sharedPhone') || 'Telefone'}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.email || ''}
                      onChange={(e) => setItem({ ...item, email: e.target.value })}
                      label={t('sharedEmail') || 'Email'}
                    />
                  </Box>
                </Box>

                {/* Segunda linha */}
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.document || ''}
                      onChange={(e) => setItem({ ...item, document: e.target.value })}
                      label={t('sharedDocument') || 'Documento'}
                    />
                  </Box>
                  <Box flex={2} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.address || ''}
                      onChange={(e) => setItem({ ...item, address: e.target.value })}
                      label={t('sharedAddress') || 'Endereço'}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.neighborhood || ''}
                      onChange={(e) => setItem({ ...item, neighborhood: e.target.value })}
                      label={t('sharedNeighborhood') || 'Bairro'}
                    />
                  </Box>
                </Box>

                {/* Terceira linha */}
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.city || ''}
                      onChange={(e) => setItem({ ...item, city: e.target.value })}
                      label={t('sharedCity') || 'Cidade'}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.state || ''}
                      onChange={(e) => setItem({ ...item, state: e.target.value })}
                      label={t('sharedState') || 'Estado'}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.postalcode || ''}
                      onChange={(e) => setItem({ ...item, postalcode: e.target.value })}
                      label={t('sharedPostalCode') || 'CEP'}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Seção Preferências */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedPreferences') || 'Preferências'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                width="100%"
              >
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.whatsapp || ''}
                      onChange={(e) => setItem({ ...item, whatsapp: e.target.value })}
                      label={t('sharedWhatsapp') || 'WhatsApp'}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.twitter || ''}
                      onChange={(e) => setItem({ ...item, twitter: e.target.value })}
                      label={t('sharedTwitter') || 'Twitter'}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.instagram || ''}
                      onChange={(e) => setItem({ ...item, instagram: e.target.value })}
                      label={t('sharedInstagram') || 'Instagram'}
                    />
                  </Box>
                </Box>
                <Box
                  display="flex"
                  width="100%"
                  gap={2}
                  sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.facebook || ''}
                      onChange={(e) => setItem({ ...item, facebook: e.target.value })}
                      label={t('sharedFacebook') || 'Facebook'}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.googlekey || ''}
                      onChange={(e) => setItem({ ...item, googlekey: e.target.value })}
                      label={t('sharedGoogleKey') || 'Chave do Google'}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Seção Configurações Personalizadas */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedRevendaCustom') || 'Configurações Personalizadas'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                width="100%"
                gap={2}
                sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
              >
                {[1, 2, 3, 4].map((num) => {
                  const field = `color${num}`;
                  const defaultValue = num === 3 ? '#cccccc' : '#1976d2';
                  return (
                    <Box key={num} flex={1} minWidth={0}>
                      <TextField
                        fullWidth
                        margin="normal"
                        value={item[field] || defaultValue}
                        onChange={(e) => setItem({ ...item, [field]: e.target.value })}
                        label={`${t('attributeColor')} ${num}`}
                        InputProps={{
                          startAdornment: (
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: item[field] || defaultValue,
                                marginRight: 1,
                                border: '1px solid #ccc',
                                cursor: 'pointer'
                              }}
                              onClick={(e) => handleColorClick(field, e)}
                            />
                          ),
                        }}
                      />
                    </Box>
                  );
                })}

                {/* Campo Background */}
                <Box flex={1} minWidth={0}>
                  <TextField
                    fullWidth
                    margin="normal"
                    value={item.background || '#ffffff'}
                    onChange={(e) => {
                      setItem({ ...item, background: e.target.value });
                      verifyBackgrounds();
                    }}
                    label={t('attributeBackground') || 'Background'}
                    InputProps={{
                      startAdornment: (
                        <Tooltip
                          title={backgroundAvailable.light || backgroundAvailable.dark ?
                            "Imagem de fundo disponível" : "Gradiente padrão"}
                          arrow
                        >
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '4px',
                              background: backgroundAvailable.light || backgroundAvailable.dark ?
                                `url(./themes/${item.code}/imagens/${currentMode}-background.webp)` :
                                currentMode === 'dark' ? darkPattern : lightPattern,
                              backgroundSize: 'cover',
                              marginRight: 1,
                              border: '1px solid #ccc',
                              cursor: 'pointer',
                              position: 'relative',
                              overflow: 'hidden'
                            }}
                            onClick={(e) => handleColorClick('background', e)}
                          >
                            {!(backgroundAvailable.light || backgroundAvailable.dark) && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: item.background || '#ffffff',
                                  opacity: 0.3
                                }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      ),
                    }}
                  />
                </Box>
              </Box>

              {/* Popover do Color Picker */}
              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleColorClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <ChromePicker
                  color={item[currentColorField] || '#1976d2'}
                  onChange={handleColorChange}
                />
              </Popover>
            </AccordionDetails>
          </Accordion>

          {/* Seção Aplicativos */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedApps') || 'Aplicativos'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                width="100%"
                gap={2}
                sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
              >
                <Box flex={1} minWidth={0}>
                  <TextField
                    fullWidth
                    margin="normal"
                    value={item.googleplay || ''}
                    onChange={(e) => setItem({ ...item, googleplay: e.target.value })}
                    label={t('sharedGooglePlay') || 'Google Play'}
                  />
                </Box>
                <Box flex={1} minWidth={0}>
                  <TextField
                    fullWidth
                    margin="normal"
                    value={item.applestore || ''}
                    onChange={(e) => setItem({ ...item, applestore: e.target.value })}
                    label={t('sharedAppleStore') || 'Apple Store'}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Nova Seção Ícones */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedIcons') || 'Ícones'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                width="100%"
              >
                <Typography variant="body2" color="textSecondary">
                  {t('sharedIconsDescription') || 'Upload de imagem para gerar ícones da revenda (PWA, favicon, etc.)'}
                </Typography>

                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="icon-upload"
                  type="file"
                  onChange={handleIconInput}
                />
                <label htmlFor="icon-upload">
                  <Button variant="contained" component="span">
                    {t('sharedSelectIcon') || 'Selecionar Ícone'}
                  </Button>
                </label>

                {getIconPreview() && (
                  <Box mt={2}>
                    <Typography variant="body2">
                      {t('sharedPreview') || 'Preview:'}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" mt={1}>
                      <img
                        src={getIconPreview()}
                        alt="Icon preview"
                        className={classes.iconPreview}
                      />
                    </Box>
                  </Box>
                )}

                {item?.attributes?.iconFileName && (
                  <Typography variant="caption">
                    {t('sharedCurrentFile') || 'Arquivo atual:'} {item.attributes.iconFileName}
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          <EditAttributesAccordion
            attributes={item.attributes}
            setAttributes={(attributes) => setItem({ ...item, attributes })}
            definitions={{}}
          />
        </>
      )}
    </EditItemView>
  );
};

export default RevendaPage;